import { HelmetProvider } from "react-helmet-async";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { StyledEngineProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";

// utils...
import { lightTheme } from "./theme/theme";
import useConsole from "src/utils/hooks/useConsole";
import { getRuntimeConfig } from "./getRuntimeConfig";

// error handling
import ErrorBoundary from "./pages/ErrorBoundary";
import { Routing } from "./Routes";
import { ReduxProvider } from "./providers/ReduxProvider";
import { GaConfig } from "./analytics/GaConfig";
import NetworkStatus from "./utils/NetworkStatus";
import { AuthProvider } from "./providers/AuthProvider";
import { initAmplitude } from "./Amplitude";
import LOGDNAWrapper from "./utils/LogDnaWrapper";
import { TSAccountProtectionProvider } from "@transmitsecurity/riskid-reactjs-ts";
import { AdoraIntegration } from "./analytics/AdoraIntegration";
import { useCallback } from "react";
import { setTransmitInitError } from "src/utils/transmitInitError";

const clientId =
  getRuntimeConfig().CNB_ENV === "main" || getRuntimeConfig().CNB_ENV === "release"
    ? "1c3c4921.67eb1fe1.EXYdsornYJxbGI7JiOGFN.riskid.security"
    : "e85b89a9.79eb1e68.guaw9yvxhkko4e32ionfa.riskid.security";

/**
 * Wraps TSAccountProtectionProvider with an onError handler that caches any
 * SDK initialisation error in memory. Nothing is sent to the backend here —
 * the error is only reported to riskRecommendations when executeTransmitRisk
 * is later called (i.e. the user is actively logging in or performing an
 * action), at which point an authenticated session exists.
 */
const TSAccountProtectionWrapper = ({ children }: { children: React.ReactNode }) => {
  const handleError = useCallback((err: any) => {
    const reason =
      err?.message || (typeof err === "string" ? err : "TSAccountProtection initialisation error");
    console.error("[TSAccountProtection] SDK initialisation error:", err);
    setTransmitInitError({
      reason,
      stack: err?.stack || JSON.stringify(err) || "Not available",
    });
  }, []);

  return (
    <TSAccountProtectionProvider clientId={clientId} options={{ onError: handleError }}>
      {children}
    </TSAccountProtectionProvider>
  );
};

const App = () => {
  initAmplitude();
  useConsole();

  return (
    <HelmetProvider>
      <ReduxProvider>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={lightTheme}>
            <NetworkStatus>
              <CssBaseline />
              <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={"en"}>
                <LOGDNAWrapper>
                  <ErrorBoundary>
                    <TSAccountProtectionWrapper>
                      <AuthProvider>
                        <GaConfig />
                        <Routing />
                        <AdoraIntegration />
                      </AuthProvider>
                    </TSAccountProtectionWrapper>
                  </ErrorBoundary>
                </LOGDNAWrapper>
              </LocalizationProvider>
            </NetworkStatus>
          </ThemeProvider>
        </StyledEngineProvider>
      </ReduxProvider>
    </HelmetProvider>
  );
};
export default App;
